import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography, Flex } from 'antd';
import { IconFileTypeCsv } from '@tabler/icons-react';
import type { Condition as MatchyCondition } from 'csv_matchy/dist/models/classes/condition';
import type { FieldTypeValue } from 'csv_matchy/dist/models/enums/field_type';
import {
  CsvColumnDefinition,
  ParsedCsvRow,
} from '~/app/components/knowledgeBase/componentSchemaManager/ComponentSchemaManager.types';
import type {
  CsvMatchySubmitData,
  CsvMatchyUploadProps,
} from './CsvMatchyUpload.types';

const { Text } = Typography;

type MatchyInstance = HTMLElement & {
  shadow: ShadowRoot;
  init: () => void;
  buildTable: () => void;
  displayElementById: (id: string) => void;
  context: { preSubmitFileContext: boolean };
  fileHeader: string[];
  rows: string[][];
  values: Map<string, string>;
  onSelectOption: (select: HTMLSelectElement, th: HTMLElement) => void;
  validateFile: (file: File | undefined) => boolean;
  uploadFile: () => Promise<void>;
  submit: (data: { lines: Map<string, { value: string }>[] }) => Promise<void>;
};

export const CsvMatchyUpload = ({
  config,
  onSubmitDataAction,
  onFileLoadedAction,
  onMappingChangeAction,
}: CsvMatchyUploadProps) => {
  const { t } = useTranslation('componentSchema');
  const containerRef = useRef<HTMLDivElement>(null);
  const matchyInstanceRef = useRef<MatchyInstance | null>(null);
  const onSubmitDataActionRef = useRef(onSubmitDataAction);
  const onFileLoadedActionRef = useRef(onFileLoadedAction);
  const onMappingChangeActionRef = useRef(onMappingChangeAction);

  useEffect(() => {
    onSubmitDataActionRef.current = onSubmitDataAction;
  }, [onSubmitDataAction]);

  useEffect(() => {
    onFileLoadedActionRef.current = onFileLoadedAction;
  }, [onFileLoadedAction]);

  useEffect(() => {
    onMappingChangeActionRef.current = onMappingChangeAction;
  }, [onMappingChangeAction]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let disposed = false;

    const mount = async () => {
      const { Matchy } = await import('csv_matchy/dist/main');
      const { Option } = await import('csv_matchy/dist/models/classes/option');
      const { FieldType } =
        await import('csv_matchy/dist/models/enums/field_type');
      const { Condition } =
        await import('csv_matchy/dist/models/classes/condition');
      const { ConditonProperty } =
        await import('csv_matchy/dist/models/enums/conditon_property');

      if (disposed) return;

      const fieldTypeMap: Record<
        NonNullable<CsvColumnDefinition['fieldType']>,
        FieldTypeValue
      > = {
        float: FieldType.float,
        integer: FieldType.integer,
        string: FieldType.string,
        bool: FieldType.bool,
        stringArray: FieldType.string,
      };

      const parseFileRows = async (
        file: File,
      ): Promise<{ headers: string[]; rows: string[][] }> => {
        const lowerName = file.name.toLowerCase();

        if (lowerName.endsWith('.csv')) {
          const text = await file.text();

          return new Promise((resolve, reject) => {
            Papa.parse<string[]>(text, {
              skipEmptyLines: true,
              complete: (results) => {
                const lines = results.data
                  .map((line) =>
                    line.map((value) => String(value ?? '').trim()),
                  )
                  .filter((line) =>
                    line.some((value) => value.trim().length > 0),
                  );

                const [headerRow = [], ...bodyRows] = lines;
                resolve({ headers: headerRow, rows: bodyRows });
              },
              error: (error: Error) => reject(error),
            });
          });
        }

        if (lowerName.endsWith('.xlsx')) {
          const data = new Uint8Array(await file.arrayBuffer());
          const workbook = XLSX.read(data, { type: 'array' });
          const firstSheetName = workbook.SheetNames[0];

          if (!firstSheetName) {
            return { headers: [], rows: [] };
          }

          const worksheet = workbook.Sheets[firstSheetName];
          const lines = XLSX.utils.sheet_to_json<
            (string | number | boolean | null)[]
          >(worksheet, { header: 1, defval: '' });

          const normalizedLines = lines
            .map((line) => line.map((value) => String(value ?? '').trim()))
            .filter((line) => line.some((value) => value.trim().length > 0));

          const [headerRow = [], ...bodyRows] = normalizedLines;
          return { headers: headerRow, rows: bodyRows };
        }

        throw new Error('Only CSV and XLSX files are supported');
      };

      const applySuggestions = (
        matchy: MatchyInstance,
        headers: string[],
        suggestions: Record<string, string>,
      ) => {
        headers.forEach((header, index) => {
          const suggestedField = suggestions[header];
          if (!suggestedField) {
            return;
          }

          const select = matchy.shadow.querySelector<HTMLSelectElement>(
            `select[id="${index}"]`,
          );
          const th = select?.closest('th');

          if (!select || !(th instanceof HTMLElement)) {
            return;
          }

          const hasOption = Array.from(select.options).some(
            (option) => option.value === suggestedField,
          );
          if (!hasOption) {
            return;
          }

          select.value = suggestedField;
          matchy.onSelectOption(select, th);
        });
      };

      const emitMappingChange = (matchy: MatchyInstance) => {
        const mapping = matchy.fileHeader.reduce<Record<string, string>>(
          (accumulator, header, index) => {
            const mappedField = matchy.values.get(String(index));
            if (!mappedField) {
              return accumulator;
            }

            accumulator[header] = mappedField;
            return accumulator;
          },
          {},
        );

        onMappingChangeActionRef.current?.({
          headers: [...matchy.fileHeader],
          rows: matchy.rows.map((row) => [...row]),
          mapping,
        });
      };

      const options = config.columns.map((col) => {
        const conditions: MatchyCondition[] = [];

        if (col.regex) {
          conditions.push(
            new Condition(
              ConditonProperty.regex,
              col.regex,
              undefined,
              col.regexMessage ?? null,
            ),
          );
        }

        return new Option(
          col.title,
          col.key,
          col.required,
          fieldTypeMap[col.fieldType ?? 'string'] ?? FieldType.string,
          conditions,
        );
      });

      if (container.querySelector('app-matchy')) return;

      const matchy = new Matchy(options) as unknown as MatchyInstance;

      const originalOnSelectOption = matchy.onSelectOption.bind(matchy);
      matchy.onSelectOption = (select: HTMLSelectElement, th: HTMLElement) => {
        originalOnSelectOption(select, th);
        emitMappingChange(matchy);
      };

      const fileInput = matchy.shadow.getElementById('csvFile');
      if (fileInput instanceof HTMLInputElement) {
        fileInput.accept = '.csv,.xlsx';
      }

      matchy.validateFile = (file: File | undefined) => {
        if (!file) {
          window.alert('Please select a file.');
          return false;
        }

        const lowerName = file.name.toLowerCase();
        if (!lowerName.endsWith('.csv') && !lowerName.endsWith('.xlsx')) {
          window.alert('Please select a CSV or XLSX file.');
          return false;
        }

        return true;
      };

      matchy.uploadFile = async () => {
        matchy.init();
        matchy.context.preSubmitFileContext = false;

        const uploadInput = matchy.shadow.getElementById(
          'csvFile',
        ) as HTMLInputElement | null;
        const inputFile = uploadInput?.files?.[0];

        if (!matchy.validateFile(inputFile)) {
          return;
        }

        if (!inputFile) {
          return;
        }

        const { headers, rows } = await parseFileRows(inputFile);
        matchy.fileHeader = headers;
        matchy.rows = rows;
        matchy.buildTable();
        matchy.displayElementById('preSubmit');
        emitMappingChange(matchy);

        const suggestions = await onFileLoadedActionRef.current?.({
          fileName: inputFile.name,
          headers,
          rows,
        });

        if (suggestions) {
          applySuggestions(matchy, headers, suggestions);
          emitMappingChange(matchy);
        }
      };

      matchy.submit = async (data: CsvMatchySubmitData) => {
        const rows = data.lines.reduce<ParsedCsvRow[]>((acc, line) => {
          const row = Array.from(line.entries()).reduce<ParsedCsvRow>(
            (rowAcc, [key, cell]) => ({ ...rowAcc, [key]: cell.value }),
            {},
          );
          return [...acc, row];
        }, []);
        onSubmitDataActionRef.current(rows);
      };

      matchyInstanceRef.current = matchy;
      container.appendChild(matchy);
    };

    mount();

    return () => {
      disposed = true;
      if (container) {
        const el = container.querySelector('app-matchy');
        if (el) container.removeChild(el);
      }
      matchyInstanceRef.current = null;
    };
  }, [config.columns]);

  return (
    <div>
      <Flex align="center" gap={8} style={{ marginBottom: 12 }}>
        <IconFileTypeCsv size={18} color="#1E6DF6" />
        <Text type="secondary" style={{ fontSize: 13 }}>
          {t('matchy.hint')}
        </Text>
      </Flex>
      <div
        ref={containerRef}
        style={{
          border: '1px solid #e8e8e8',
          borderRadius: 8,
          padding: 16,
          background: '#fafafa',
          minHeight: 200,
          maxWidth: '100%',
          overflowX: 'auto',
        }}
      />
    </div>
  );
};
